import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import JitsiCall from "@/components/JitsiCall";
import { Video, Loader2, Copy, PhoneOff } from "lucide-react";

export default function TeleconsultTab({ patientId }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(false);

  const patientLink = session ? `${window.location.origin}/tele/${session.room}?nome=Paciente` : "";

  const start = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/patients/${patientId}/teleconsults`, {});
      setSession(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha ao iniciar teleconsulta");
    } finally { setLoading(false); }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(patientLink); toast.success("Link copiado"); }
    catch { toast.error("Não foi possível copiar"); }
  };

  const end = () => setSession(null);

  return (
    <div className="space-y-6" data-testid="teleconsult-tab">
      <div>
        <h3 className="font-heading text-xl font-medium flex items-center gap-2"><Video className="w-5 h-5 text-ai" /> Teleconsulta</h3>
        <p className="text-sm text-muted-foreground">Atenda por vídeo — médico e paciente na mesma tela, dentro do sistema.</p>
      </div>

      {!session ? (
        <Card className="p-10 text-center border-dashed" data-testid="teleconsult-start-card">
          <Video className="w-10 h-10 mx-auto mb-4 text-ai" />
          <p className="text-muted-foreground mb-4">Inicie a sala de vídeo e partilhe o link com o paciente.</p>
          <Button onClick={start} disabled={loading} data-testid="teleconsult-start-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Video className="w-4 h-4 mr-2" />}
            Iniciar teleconsulta
          </Button>
        </Card>
      ) : (
        <div className="space-y-4">
          <Card className="p-3 flex items-center gap-2 flex-wrap" data-testid="teleconsult-link-card">
            <span className="text-sm text-muted-foreground shrink-0">Link do paciente:</span>
            <Input readOnly value={patientLink} className="font-mono-data text-xs flex-1 min-w-[220px]" data-testid="teleconsult-link-input" />
            <Button size="sm" variant="outline" onClick={copyLink} data-testid="teleconsult-copy-btn"><Copy className="w-4 h-4 mr-1" /> Copiar</Button>
            <Button size="sm" variant="destructive" onClick={end} data-testid="teleconsult-end-btn"><PhoneOff className="w-4 h-4 mr-1" /> Encerrar</Button>
          </Card>
          <div className="rounded-lg overflow-hidden border border-border" style={{ height: "70vh" }} data-testid="teleconsult-video">
            <JitsiCall room={session.room} displayName="Médico(a)" onEnded={end} />
          </div>
        </div>
      )}
    </div>
  );
}
